import React, { useMemo } from 'react';
import {
  ListOfVehicles,
  VehicleData,
  CardContentWrapper,
  CardWrapper,
  ViewAllResults,
  NoResultsHeader,
} from './SidewideSearch.styles';
import { Card, Headline, Icon, Text } from '@oneaudi/unified-web-components';
import type { InventoryGroup } from '../../../types';
import { HOST_NAME } from '../../constants';
import CPOLogo from '../../assets/CpoBadge';
import VehicleCard from './VehicleCard';

interface VehicleListProps {
  vehiclesData: InventoryGroup | undefined;
  type: 'NEW' | 'USED' | 'CPO';
  showPrice: boolean;
}

const VehicleList: React.FC<VehicleListProps> = ({ vehiclesData, type, showPrice }) => {
  const vehicles = vehiclesData?.vehicles;
  const totalCount = vehiclesData?.totalCount;
  const vlpLink = vehiclesData?.vlpLink;

  const vlpRedirectUrl = `${HOST_NAME}${vlpLink}`;
  const isCpoVehicles = type === 'CPO';
  const isUsedVehicle = type === 'USED';
  const isOneResult = totalCount === 1;

  const hasTechnicalError = vehiclesData?.code && vehiclesData?.reason?.includes('error');

  const ErrorState = useMemo(() => {
    return (
      <NoResultsHeader>
        <Headline
          tag="h3"
          variant={{
            order: '3',
            type: 'headline',
            weight: 'normal',
          }}
        >
          Results couldn’t be loaded.
        </Headline>
        <Text>Try again, or browse all available options on our vehicle listing pages.</Text>
      </NoResultsHeader>
    );
  }, []);

  const vlpLabel = useMemo(() => {
    if (isUsedVehicle) return 'Pre-owned vehicle';
    if (isCpoVehicles) return 'Certified vehicle';
    return 'New vehicle';
  }, [isCpoVehicles, isUsedVehicle]);

  const vlpRedirectCta = useMemo(() => {
    return (
      <CardWrapper tabIndex={0} href={vlpRedirectUrl} vlpCard>
        <Card margin={[0, 0, 0, 0]}>
          <CardContentWrapper>
            <Icon name="traffic" size="l" />
            <VehicleData gap={4} vlpCard>
              <Text>{vlpLabel}</Text>
              <ViewAllResults>
                <Text>
                  View {isOneResult ? '' : 'all'} {totalCount} {isOneResult ? 'result' : 'results'}
                </Text>
                <Icon name="forward" size="s" />
              </ViewAllResults>
            </VehicleData>
          </CardContentWrapper>
        </Card>
      </CardWrapper>
    );
  }, [isOneResult, totalCount, vlpLabel, vlpRedirectUrl]);

  return (
    <ListOfVehicles>
      {isCpoVehicles && <CPOLogo type="CPO_USA" />}
      {hasTechnicalError && ErrorState}
      {vehicles?.map((vehicle) => {
        return <VehicleCard key={vehicle.id} vehicle={vehicle} showPrice={showPrice} type={type} />;
      })}
      {vlpRedirectCta}
    </ListOfVehicles>
  );
};

export default VehicleList;
